import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import Modal from '@perpay-web/components/base/Modal/Modal';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import { noop } from '@perpay-web/utils/noop';

import styles from './CardApplicationConfirmAddress.scss';

/**
 * Modal for confirming address information during card application
 * @param {Object} props - Component props
 * @param {string} props.street1 - Primary street address
 * @param {string} [props.street2] - Secondary address line (apt/suite)
 * @param {string} props.city - City name
 * @param {string} props.state - State abbreviation
 * @param {string} props.zipcode - ZIP code
 * @param {Function} [props.onClose] - Callback when modal is closed or edit button is clicked
 * @param {Function} [props.onContinue] - Callback when continue without editing is clicked
 * @param {boolean} [props.isLoading] - Loading state for buttons
 * @returns {JSX.Element} Modal component
 */
const CardApplicationConfirmAddress = ({
    street1,
    street2,
    city,
    state,
    zipcode,
    onClose = noop,
    onContinue = noop,
    isLoading = false,
}) => (
    <Modal
        className={styles.modal}
        containerClassName={styles['modal-container']}
        showClose
        onClose={onClose}
        aria-label='Confirm Address'
    >
        <Stack className={styles['modal-content-container']}>
            <Stack className={styles['modal-content']}>
                <Stack className={styles['modal-content__title']}>
                    <div className={styles.title}>Confirm Address</div>
                    <div className={styles.subtitle}>
                        We have identified your address has a missing or
                        incorrect <strong>apartment or suite</strong>. If this
                        information is not added, your card might be unable to
                        be delivered. Please review your address and edit if
                        needed.
                    </div>
                </Stack>
                <Stack className={styles['address-section']}>
                    <div className={styles['address-label']}>You Entered:</div>
                    <div className={styles['address-display']}>
                        <div className={styles['address-line-1']}>
                            {street1}
                            {street2 ? `, ${street2}` : ''}
                        </div>
                        <div className={styles['address-line-2']}>
                            {city}, {state}, {zipcode}
                        </div>
                    </div>
                </Stack>
            </Stack>

            <Stack className={styles.buttons}>
                <CustomButton
                    onClick={onClose}
                    loading={isLoading}
                    className={styles['edit-button']}
                >
                    Edit Address
                </CustomButton>
                <CustomButton
                    action='inline-link'
                    className={styles['continue-button']}
                    onClick={onContinue}
                >
                    Continue without editing
                </CustomButton>
            </Stack>
        </Stack>
    </Modal>
);

export default CardApplicationConfirmAddress;
